/**
 * Forces muted slideshow slide videos to autoplay on all devices.
 *
 * Mobile browsers (and some desktop policies) may block autoplay until a user
 * gesture. Any interaction counts — cookie banners, newsletter popups, taps,
 * clicks, or key presses — and will unlock / retry playback.
 */

const SLIDE_VIDEO_SELECTOR = 'video.slide__video';
const UNLOCK_EVENTS = /** @type {const} */ (['pointerdown', 'touchstart', 'click', 'keydown']);

class SlideVideoAutoplay {
  /** @type {boolean} */
  static #started = false;

  /** @type {WeakSet<HTMLVideoElement>} */
  static #bound = new WeakSet();

  /** @type {IntersectionObserver | null} */
  static #visibilityObserver = null;

  static init() {
    if (this.#started || typeof document === 'undefined') return;
    this.#started = true;

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const video = /** @type {HTMLVideoElement} */ (entry.target);
          if (entry.isIntersecting) {
            this.#tryPlay(video);
          }
        }
      },
      { threshold: 0.15 }
    );

    this.#scan(document);

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.#scan(/** @type {Element} */ (node));
          }
        }
      }
    });
    mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

    // Capture phase so interactions on overlays (cookies, popups) still unlock autoplay.
    const unlock = () => this.#playAll();
    for (const type of UNLOCK_EVENTS) {
      document.addEventListener(type, unlock, { capture: true, passive: true });
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.#playAll();
    });

    window.addEventListener('pageshow', () => this.#playAll());
  }

  /**
   * @param {ParentNode | Element} root
   */
  static #scan(root) {
    /** @type {HTMLVideoElement[]} */
    const videos = [];

    if (root instanceof HTMLVideoElement && root.matches(SLIDE_VIDEO_SELECTOR)) {
      videos.push(root);
    } else if ('querySelectorAll' in root) {
      videos.push(.../** @type {NodeListOf<HTMLVideoElement>} */ (root.querySelectorAll(SLIDE_VIDEO_SELECTOR)));
    }

    for (const video of videos) {
      this.#setup(video);
    }
  }

  /**
   * @param {HTMLVideoElement} video
   */
  static #setup(video) {
    if (this.#bound.has(video)) return;
    this.#bound.add(video);

    this.#prepare(video);
    this.#tryPlay(video);

    video.addEventListener('loadeddata', () => this.#tryPlay(video));
    video.addEventListener('canplay', () => this.#tryPlay(video));
    video.addEventListener('suspend', () => this.#tryPlay(video));

    this.#visibilityObserver?.observe(video);
  }

  /**
   * @param {HTMLVideoElement} video
   */
  static #prepare(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.loop = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.removeAttribute('controls');
  }

  /**
   * @param {HTMLVideoElement} video
   */
  static #tryPlay(video) {
    if (!video.isConnected) return;

    this.#prepare(video);

    if (!video.paused && !video.ended) return;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // Autoplay blocked until a user gesture — unlock listeners will retry.
      });
    }
  }

  static #playAll() {
    const videos = /** @type {NodeListOf<HTMLVideoElement>} */ (document.querySelectorAll(SLIDE_VIDEO_SELECTOR));
    for (const video of videos) {
      this.#setup(video);
      this.#tryPlay(video);
    }
  }
}

SlideVideoAutoplay.init();
